<template>
<!-- Botón para mostrar/ocultar contenido SEO (Desktop) -->
<div v-if="searchPerformed && resultsCount > 0" class="px-6 py-4 border-t border-gray-200 bg-white">
  <button 
    type="button"
    @click="showOpen = !showOpen"
    class="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
  >
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      class="h-5 w-5 transition-transform duration-200"
      :class="{ 'rotate-180': showOpen }"
      viewBox="0 0 20 20" 
      fill="currentColor"
    >
      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
    </svg>
    <span v-if="!showOpen">
      {{ currentCity ? `Ver más sobre ${currentCity}` : 'Ver más información' }} · FAQs
    </span>
    <span v-else>Ocultar información adicional</span>
  </button>
</div>

<!-- SEO Content Section (Desktop) - Visible en páginas de ciudad o cuando se expande manualmente -->
<section v-if="searchPerformedOrCity && showOpen"
  class="px-6 py-8 bg-gradient-to-b from-white to-gray-50 border-t border-gray-100">
  
  <!-- Contenido cuando NO hay resultados -->
  <div v-if="resultsCount === 0" class="max-w-4xl">
    <h2 class="text-xl font-bold text-gray-900 mb-4">
      <template v-if="regionId === 'us'">
        {{ currentCity ? `Coliving in ${currentCity}` : 'Coliving in New York City' }}
      </template>
      <template v-else-if="regionId === 'uk'">
        {{ currentCity ? `Coliving in ${currentCity}` : 'Coliving in London' }}
      </template>
      <template v-else>
        {{ currentCity ? `Colivings en ${currentCity}` : 'Colivings en España' }}
      </template>
    </h2>

    <p class="text-gray-600 leading-relaxed mb-6">
      <template v-if="regionId === 'us'">
        {{ currentCity
          ? `We're updating our coliving database for ${currentCity}. Hundreds of verified rooms are available in other New York neighbourhoods.`
          : `We're updating our database. Adjust your filters or explore other New York neighbourhoods with verified coliving spaces.`
        }}
      </template>
      <template v-else-if="regionId === 'uk'">
        {{ currentCity
          ? `We're updating our coliving database for ${currentCity}. Hundreds of verified rooms are available in other London neighbourhoods.`
          : `We're updating our database. Adjust your filters or explore other London neighbourhoods with verified coliving spaces.`
        }}
      </template>
      <template v-else>
        {{ currentCity
          ? `Actualmente estamos actualizando nuestra base de datos de colivings en ${currentCity}. Miles de habitaciones verificadas están disponibles en otras ciudades de España.`
          : 'Estamos actualizando nuestra base de datos. Ajusta tus filtros o explora otras ciudades con colivings verificados.'
        }}
      </template>
    </p>

    <!-- CTA para explorar otras opciones -->
    <div class="bg-indigo-50 rounded-lg p-6 mb-6">
      <template v-if="regionId === 'us'">
        <h3 class="text-lg font-semibold text-indigo-900 mb-3">Explore other neighbourhoods</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <router-link
            v-for="n in ['Manhattan', 'Brooklyn', 'Queens', 'Jersey City']"
            :key="n"
            :to="`${searchBasePath}/${n.toLowerCase().replace(' ', '-')}`"
            class="px-4 py-2 bg-white text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors text-center border border-indigo-200"
          >{{ n }}</router-link>
        </div>
      </template>
      <template v-else-if="regionId === 'uk'">
        <h3 class="text-lg font-semibold text-indigo-900 mb-3">Explore other neighbourhoods</h3>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <router-link
            v-for="n in ['Shoreditch', 'Canary Wharf', 'Brixton', 'Hackney', 'Clapham', 'Battersea']"
            :key="n"
            :to="`${searchBasePath}/${n.toLowerCase().replace(/ /g, '-')}`"
            class="px-4 py-2 bg-white text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors text-center border border-indigo-200"
          >{{ n }}</router-link>
        </div>
      </template>
      <template v-else>
        <h3 class="text-lg font-semibold text-indigo-900 mb-3">Explora otras opciones disponibles</h3>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <router-link
            v-for="city in ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Málaga', 'Salamanca']"
            :key="city"
            :to="`${searchBasePath}/${city.toLowerCase()}`"
            class="px-4 py-2 bg-white text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors text-center border border-indigo-200"
          >{{ city }}</router-link>
        </div>
      </template>
    </div>

    <!-- Info sobre el servicio -->
    <div class="border-t border-gray-200 pt-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-3">
        {{ (regionId === 'us' || regionId === 'uk') ? "What you'll find on Coolliving" : '¿Qué encontrarás en Coolliving?' }}
      </h3>
      <ul class="space-y-2">
        <template v-if="regionId === 'us'">
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Hundreds of verified coliving spaces across New York City</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Rooms from $1,200/mo, all-inclusive (WiFi, utilities, cleaning)</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Book 100% online — no agency fees, no commissions</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Flexible lease terms tailored to your needs</span>
          </li>
        </template>
        <template v-else-if="regionId === 'uk'">
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Hundreds of verified coliving spaces across London</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Rooms from £800/mo, all bills included (WiFi, utilities, cleaning)</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Book 100% online — no agency fees, no commissions</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-gray-700">Flexible tenancy agreements tailored to your needs</span>
          </li>
        </template>
        <template v-else>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-gray-700">Miles de colivings verificados en toda España</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-gray-700">Precios desde 350€/mes con todo incluido</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-gray-700">Reserva 100% online sin comisiones</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-gray-700">Contratos flexibles adaptados a tus necesidades</span>
          </li>
        </template>
      </ul>
    </div>
  </div>

  <!-- Contenido cuando SÍ hay resultados -->
  <div v-else>
    <!-- Contenido específico de PROVEEDOR -->
    <div v-if="currentProviderContent && isProviderSearch" class="max-w-4xl">
      <h2 class="text-xl font-bold text-gray-900 mb-4">
        {{ currentProviderContent.fullName }}{{ currentCity ? ` en ${currentCity}` : ' en España' }}
      </h2>

      <p class="text-gray-600 leading-relaxed mb-6">
        {{ currentProviderContent.description }}
      </p>

      <!-- Info cards del proveedor -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Precio medio</p>
          <p class="text-lg font-bold text-indigo-600">{{ currentProviderContent.avgPrice }}€/mes</p>
        </div>
        <div class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Rango precios</p>
          <p class="text-lg font-bold text-gray-900">{{ currentProviderContent.priceRange }}</p>
        </div>
        <div class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Disponibles</p>
          <p class="text-lg font-bold text-green-600">{{ resultsCount }}</p>
        </div>
        <div class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Ciudades</p>
          <p class="text-lg font-bold text-purple-600">{{ currentProviderContent.cities.length }}</p>
        </div>
      </div>

      <!-- Ciudades donde opera -->
      <div v-if="currentProviderContent.cities?.length && !currentCity" class="mb-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-2">Ciudades disponibles</h3>
        <div class="flex flex-wrap gap-2">
          <router-link v-for="city in currentProviderContent.cities" :key="city"
            :to="`/encuentratucoliving/${currentProvider}/${city.toLowerCase()}`"
            class="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm hover:bg-indigo-100 transition-colors">
            {{ city }}
          </router-link>
        </div>
      </div>

      <!-- Highlights del proveedor -->
      <div v-if="currentProviderContent.highlights?.length" class="mb-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-2">¿Por qué {{ currentProviderContent.fullName }}?
        </h3>
        <ul class="grid grid-cols-2 gap-2">
          <li v-for="highlight in currentProviderContent.highlights" :key="highlight"
            class="flex items-center text-sm text-gray-600">
            <svg class="h-4 w-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd" />
            </svg>
            {{ highlight }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Aviso: ciudad estudiante sin página SEO dedicada (noindex) -->
    <div
      v-if="showUnindexedStudentNotice"
      class="mb-6 max-w-4xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      Mostramos alojamiento para estudiantes en {{ currentCity }}.
      Para más opciones y guías de la ciudad, visita
      <router-link
        :to="`/encuentratucoliving/${citySlug}`"
        class="font-medium underline hover:text-amber-950"
      >
        colivings en {{ currentCity }}
      </router-link>.
    </div>

    <!-- Hub marketplace B2C (sin ciudad) — contenido indexable para /encuentratucoliving y /findyourcoliving -->
    <div v-else-if="!isStudentMode && !isProviderSearch && !currentCity" class="max-w-4xl">
      <template v-if="regionId === 'uk'">
        <h2 class="text-xl font-bold text-gray-900 mb-4">
          Find Coliving in London — {{ hubListingsLabel }} Verified Rooms
        </h2>
        <p class="text-gray-600 leading-relaxed mb-6">
          Compare furnished coliving rooms across London: Shoreditch, Brixton, Camden, Wembley, Canary Wharf and more.
          All-inclusive from £800/mo (WiFi, bills, weekly cleaning). No agency fees — book 100% online.
        </p>
        <div class="bg-indigo-50 rounded-lg p-6 mb-6">
          <h3 class="text-lg font-semibold text-indigo-900 mb-3">Popular London neighbourhoods</h3>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
            <router-link
              v-for="area in hubLondonAreas"
              :key="area.slug"
              :to="`/findyourcoliving/${area.slug}`"
              class="px-4 py-3 bg-white text-indigo-800 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors border border-indigo-200 text-center"
            >
              Coliving in {{ area.label }}
            </router-link>
          </div>
        </div>
        <p class="text-sm text-gray-500 mb-6">
          Read our
          <router-link to="/best-coliving-london-guide-2026" class="text-indigo-600 hover:underline font-medium">
            complete London coliving guide
          </router-link>
          for neighbourhood tips — then compare live listings here.
        </p>
      </template>

      <template v-else-if="regionId === 'us'">
        <h2 class="text-xl font-bold text-gray-900 mb-4">
          Find Coliving in New York City — Furnished Rooms from $1,200/mo
        </h2>
        <p class="text-gray-600 leading-relaxed mb-6">
          Compare verified coliving spaces in Manhattan, Brooklyn, Queens and Jersey City.
          All-inclusive furnished rooms, no broker fee. Filter by neighbourhood and move in this week.
        </p>
        <div class="bg-indigo-50 rounded-lg p-6 mb-6">
          <h3 class="text-lg font-semibold text-indigo-900 mb-3">Browse by neighbourhood</h3>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
            <router-link
              v-for="area in hubNYCAreas"
              :key="area.slug"
              :to="`/findyourcoliving/${area.slug}`"
              class="px-4 py-3 bg-white text-indigo-800 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors border border-indigo-200 text-center"
            >
              Coliving in {{ area.label }}
            </router-link>
          </div>
        </div>
        <p class="text-sm text-gray-500 mb-6">
          See our
          <router-link to="/best-coliving-new-york-guide-2026" class="text-indigo-600 hover:underline font-medium">
            NYC coliving guide
          </router-link>
          for pricing and neighbourhoods — then search live availability below.
        </p>
      </template>

      <template v-else>
        <h2 class="text-xl font-bold text-gray-900 mb-4">
          Encuentra tu Coliving en España — Habitaciones Verificadas
        </h2>
        <p class="text-gray-600 leading-relaxed mb-6">
          Compara más de 1.700 habitaciones en 14 ciudades: Madrid, Barcelona, Valencia, Málaga, Sevilla, Salamanca y más.
          Todo incluido desde 450€/mes, sin comisiones de agencia. Reserva 100% online.
        </p>
        <div class="bg-indigo-50 rounded-lg p-6 mb-6">
          <h3 class="text-lg font-semibold text-indigo-900 mb-3">Ciudades con más oferta</h3>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
            <router-link
              v-for="city in hubSpanishCities"
              :key="city.slug"
              :to="`/encuentratucoliving/${city.slug}`"
              class="px-4 py-3 bg-white text-indigo-800 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors border border-indigo-200 text-center"
            >
              Colivings en {{ city.label }}
            </router-link>
          </div>
        </div>
        <p class="text-sm text-gray-500 mb-6">
          ¿Primera vez? Lee
          <router-link to="/que-es-un-coliving" class="text-indigo-600 hover:underline font-medium">
            qué es un coliving
          </router-link>
          o explora nuestras
          <router-link to="/mejores-colivings-madrid-guia-completa-2026" class="text-indigo-600 hover:underline font-medium">
            guías por ciudad
          </router-link>.
        </p>
      </template>
    </div>

    <!-- Hub residencias estudiantes (sin ciudad seleccionada) -->
    <div v-else-if="isStudentMode && !currentCity" class="max-w-4xl">
      <h2 class="text-xl font-bold text-gray-900 mb-4">
        Residencias universitarias en España 2026
      </h2>
      <p class="text-gray-600 leading-relaxed mb-6">
        {{ studentHubSeoContent }}
      </p>
      <div class="bg-indigo-50 rounded-lg p-6 mb-6">
        <h3 class="text-lg font-semibold text-indigo-900 mb-3">Ciudades con residencias verificadas</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <router-link
            v-for="hubCity in studentHubCities"
            :key="hubCity.slug"
            :to="`/residencias/${hubCity.slug}`"
            class="px-4 py-3 bg-white text-indigo-800 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors border border-indigo-200"
          >
            <span class="block font-semibold">Residencias en {{ hubCity.label }}</span>
            <span class="block text-xs text-indigo-600 mt-0.5">Desde {{ hubCity.minPrice }} · Ver habitaciones →</span>
          </router-link>
        </div>
      </div>
      <p class="text-sm text-gray-500">
        También puedes
        <router-link to="/encuentratucoliving" class="text-indigo-600 hover:underline font-medium">
          buscar colivings en toda España
        </router-link>
        si no necesitas filtro de estudiantes.
      </p>
    </div>

    <!-- Contenido específico de ciudad -->
    <div v-else-if="currentCityContent" class="max-w-4xl">
      <h2 class="text-xl font-bold text-gray-900 mb-4">
        {{ seoH2Title }}
      </h2>

      <p class="text-gray-600 leading-relaxed mb-6">
        {{ activeSeoContent }}
      </p>

      <!-- Enlace cruzado coliving ↔ estudiantes -->
      <div
        v-if="showStudentCrossLink"
        class="mb-6 rounded-lg border border-purple-100 bg-purple-50/70 px-4 py-3"
      >
        <router-link
          :to="studentCrossLinkTo"
          class="text-sm font-medium text-purple-800 hover:text-purple-950 hover:underline"
        >
          {{ studentCrossLinkLabel }} →
        </router-link>
      </div>

      <!-- Universidades (modo estudiante o ciudad universitaria) -->
      <div
        v-if="currentCityContent.universities?.length && (isStudentMode || currentCityContent.students)"
        class="mb-6"
      >
        <h3 class="text-sm font-semibold text-gray-900 mb-2">
          {{ isStudentMode ? 'Universidades cercanas' : 'Universidades en la zona' }}
        </h3>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="uni in currentCityContent.universities"
            :key="uni"
            class="px-3 py-1 bg-purple-50 text-purple-800 rounded-full text-sm border border-purple-100"
          >
            🎓 {{ uni }}
          </span>
        </div>
      </div>

      <!-- Info cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">{{ regionId !== 'es' ? 'Avg price' : 'Precio medio' }}</p>
          <p class="text-lg font-bold text-indigo-600">
            {{ typeof currentCityContent.avgPrice === 'number' ? `${currentCityContent.avgPrice}€/mes` : currentCityContent.avgPrice }}
          </p>
        </div>
        <div class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">{{ regionId !== 'es' ? 'Price range' : 'Rango precios' }}</p>
          <p class="text-lg font-bold text-gray-900">{{ currentCityContent.priceRange }}</p>
        </div>
        <div class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Colivings</p>
          <p class="text-lg font-bold text-green-600">{{ resultsCount }} {{ regionId !== 'es' ? 'available' : 'disponibles' }}</p>
        </div>
        <div v-if="currentCityContent.students && !isStudentMode"
          class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">{{ regionId !== 'es' ? 'Type' : 'Tipo' }}</p>
          <p class="text-lg font-bold text-purple-600">🎓 {{ regionId !== 'es' ? 'University area' : 'Universitaria' }}</p>
        </div>
        <div v-else-if="isStudentMode"
          class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Perfil</p>
          <p class="text-lg font-bold text-purple-600">🎓 Estudiantes</p>
        </div>
      </div>

      <!-- Mejores zonas / Best areas -->
      <div v-if="activeZones.length" class="mb-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-2">
          {{ isStudentMode
            ? (regionId !== 'es' ? 'Best areas for student accommodation' : 'Mejores zonas para residencias de estudiantes')
            : (regionId !== 'es' ? 'Best areas for coliving' : 'Mejores zonas para coliving') }}
        </h3>
        <div class="flex flex-wrap gap-2">
          <router-link
            v-for="zone in activeZones"
            :key="zone"
            :to="getZoneLink(zone)"
            class="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm hover:bg-indigo-100 transition-colors"
          >
            {{ zone }}
          </router-link>
        </div>
        <p v-if="!isStudentMode && currentCityContent?.zoneBlogSlugs && Object.keys(currentCityContent.zoneBlogSlugs).length" class="mt-2 text-xs text-gray-500">
          <template v-if="regionId !== 'es'">Tap an area for a neighbourhood guide or city search.</template>
          <template v-else>Pulsa un barrio para ver su guía de coliving o buscar en la ciudad.</template>
        </p>
      </div>

      <!-- Highlights -->
      <div v-if="activeHighlights.length" class="mb-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-2">{{ regionId !== 'es' ? `Why ${currentCity}?` : `¿Por qué ${currentCity}?` }}</h3>
        <ul class="grid grid-cols-2 gap-2">
          <li v-for="highlight in activeHighlights" :key="highlight"
            class="flex items-center text-sm text-gray-600">
            <svg class="h-4 w-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd" />
            </svg>
            {{ highlight }}
          </li>
        </ul>
      </div>

      <!-- Guías relacionadas (SEO interno ciudad ↔ blog) — solo coliving general -->
      <div v-if="!isStudentMode && currentCityContent?.blogGuides?.length" class="mb-6 rounded-lg border border-indigo-100 bg-indigo-50/60 p-4">
        <h3 class="text-sm font-semibold text-indigo-900 mb-3">
          <template v-if="regionId === 'uk' || regionId === 'us'">
            Guides about coliving in {{ currentCity }}
          </template>
          <template v-else>
            Guías sobre coliving en {{ currentCity }}
          </template>
        </h3>
        <ul class="space-y-2">
          <li v-for="guide in currentCityContent.blogGuides" :key="guide.slug">
            <router-link
              :to="`/${guide.slug}`"
              class="text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
            >
              {{ guide.title }} →
            </router-link>
          </li>
        </ul>
      </div>
    </div>

    <!-- FAQs Section -->
    <div class="max-w-4xl mt-8 pt-8 border-t border-gray-200">
      <h3 class="text-lg font-bold text-gray-900 mb-4">
        {{ faqSectionTitle }}
      </h3>
      <div class="space-y-3">
        <div v-for="(faq, index) in visibleFaqs" :key="index"
          class="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <button
            @click="toggleFaq(index)"
            class="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors">
            <span class="font-medium text-gray-900 text-sm">{{ faq.q }}</span>
            <svg
              :class="['h-5 w-5 text-gray-400 transition-transform', expandedFaqs.includes(index) ? 'rotate-180' : '']"
              fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div v-if="expandedFaqs.includes(index)" class="px-4 pb-3 text-sm text-gray-600">
            {{ faq.a }}
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { cityInfo, isStudentCitySeoIndexed } from '../data/cityData.js';
import { quickCities, quickLondonAreas, quickNYCAreas } from '@/composables/useSearchSeoContent.js';
import { listingsLabel as LONDON_SEO_LISTINGS_LABEL } from '../../../../shared/londonMarketStats.cjs';

const hubSpanishCities = quickCities;
const hubLondonAreas = quickLondonAreas;
const hubNYCAreas = quickNYCAreas;
const hubListingsLabel = LONDON_SEO_LISTINGS_LABEL;

const showOpen = defineModel('showOpen', { type: Boolean, default: false });

const props = defineProps({
  searchPerformed: { type: Boolean, default: false },
  resultsCount: { type: Number, default: 0 },
  searchPerformedOrCity: { type: Boolean, default: false },
  currentCity: { type: String, default: null },
  regionId: { type: String, default: 'es' },
  isProviderSearch: { type: Boolean, default: false },
  currentProvider: { type: String, default: null },
  currentProviderContent: { type: Object, default: null },
  currentCityContent: { type: Object, default: null },
  seoH2Title: { type: String, default: '' },
  visibleFaqs: { type: Array, default: () => [] },
  isStudentMode: { type: Boolean, default: false },
  studentHubCities: { type: Array, default: () => [] },
  studentHubSeoContent: { type: String, default: '' },
});

const STUDENT_CROSS_LINK_CITIES = new Set([
  'Madrid', 'Barcelona', 'Valencia', 'Málaga', 'Sevilla', 'Salamanca',
]);

const searchBasePath = computed(() => {
  if (props.isStudentMode) return '/residencias';
  if (props.regionId === 'uk' || props.regionId === 'us') return '/findyourcoliving';
  return '/encuentratucoliving';
});

const citySlug = computed(() => {
  if (!props.currentCity) return '';
  return slugifyLocation(props.currentCity);
});

const activeSeoContent = computed(() => {
  if (!props.currentCityContent) return '';
  if (props.isStudentMode && props.currentCityContent.studentSeoContent) {
    return props.currentCityContent.studentSeoContent;
  }
  return props.currentCityContent.seoContent || '';
});

const activeZones = computed(() => {
  if (!props.currentCityContent) return [];
  if (props.isStudentMode && props.currentCityContent.studentZones?.length) {
    return props.currentCityContent.studentZones;
  }
  return props.currentCityContent.zones || [];
});

const activeHighlights = computed(() => {
  if (!props.currentCityContent) return [];
  if (props.isStudentMode && props.currentCityContent.studentHighlights?.length) {
    return props.currentCityContent.studentHighlights;
  }
  return props.currentCityContent.highlights || [];
});

const showStudentCrossLink = computed(() => {
  if (!props.currentCity || props.regionId !== 'es') return false;
  if (!STUDENT_CROSS_LINK_CITIES.has(props.currentCity)) return false;
  return props.isStudentMode || props.currentCityContent?.students;
});

const studentCrossLinkTo = computed(() => {
  if (props.isStudentMode) {
    return `/encuentratucoliving/${citySlug.value}`;
  }
  return `/residencias/${citySlug.value}`;
});

const studentCrossLinkLabel = computed(() => {
  if (props.isStudentMode) {
    return `Ver todos los colivings en ${props.currentCity} (no solo estudiantes)`;
  }
  return `¿Buscas residencia universitaria en ${props.currentCity}? Ver operadores verificados`;
});

const showUnindexedStudentNotice = computed(() => (
  props.isStudentMode
  && props.currentCity
  && !isStudentCitySeoIndexed(props.currentCity)
));

const faqSectionTitle = computed(() => {
  if (props.isStudentMode) {
    return props.currentCity
      ? `Preguntas frecuentes sobre residencias de estudiantes en ${props.currentCity}`
      : 'Preguntas frecuentes sobre residencias de estudiantes';
  }
  if (props.regionId !== 'es') {
    return props.currentCity ? `FAQs about coliving in ${props.currentCity}` : 'Frequently asked questions';
  }
  return props.currentCity ? `Preguntas frecuentes sobre colivings en ${props.currentCity}` : 'Preguntas frecuentes';
});

function slugifyLocation(name) {
  return name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
}

function getZoneLink(zone) {
  if (props.isStudentMode) {
    return `${searchBasePath.value}/${citySlug.value}`;
  }
  const blogSlug = props.currentCityContent?.zoneBlogSlugs?.[zone];
  if (blogSlug) return `/${blogSlug}`;
  if (props.currentCity) {
    return `${searchBasePath.value}/${citySlug.value}`;
  }
  return `${searchBasePath.value}/${slugifyLocation(zone)}`;
}

const expandedFaqs = ref([]);

function syncExpandedFaqs(city, studentMode) {
  if (!city || !cityInfo[city]) {
    expandedFaqs.value = [];
    return;
  }
  const faqs = studentMode && cityInfo[city].studentFaqs?.length
    ? cityInfo[city].studentFaqs
    : cityInfo[city].faqs;
  expandedFaqs.value = faqs ? faqs.map((_, index) => index) : [];
}

watch(
  () => [props.currentCity, props.isStudentMode],
  ([city, studentMode]) => syncExpandedFaqs(city, studentMode),
  { immediate: true },
);

function toggleFaq(index) {
  if (expandedFaqs.value.includes(index)) {
    expandedFaqs.value = expandedFaqs.value.filter((i) => i !== index);
  } else {
    expandedFaqs.value.push(index);
  }
}
</script>

